import React from 'react';
import { Navigate } from 'react-router-dom';
import { useUser } from './UserContext';

/**
 * PermissionGuard - Protects routes based on user permissions
 * 
 * @param {string} permission - The permission required: 'admin', 'canModifyData', 'maintenance', 'settings', 'transactionLog'
 * @param {ReactNode} children - The component to render if authorized
 * @param {string} redirectTo - Where to redirect if unauthorized (default: '/')
 */
const PermissionGuard = ({ permission, children, redirectTo = '/' }) => {
  const { 
    user, 
    loading, 
    isAdmin, 
    canModifyData, 
    canAccessMaintenance, 
    canAccessSettings, 
    canAccessTransactionLog 
  } = useUser();

  // Wait for user data to load
  if (loading) {
    return null; // Or return a loading spinner
  }

  // If no user, they shouldn't be here (ProtectedRoute handles this, but just in case)
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check permission
  let hasPermission = false;
  
  switch (permission) {
    case 'admin':
      hasPermission = isAdmin();
      break;
    case 'canModifyData':
      hasPermission = canModifyData();
      break;
    case 'maintenance':
      hasPermission = canAccessMaintenance();
      break;
    case 'settings':
      hasPermission = canAccessSettings();
      break;
    case 'transactionLog':
      hasPermission = canAccessTransactionLog();
      break;
    default:
      console.warn(`Unknown permission type: ${permission}`);
      hasPermission = false;
  }

  // Redirect if no permission
  if (!hasPermission) {
    return <Navigate to={redirectTo} replace />;
  }

  // Render children if authorized
  return children;
};

export default PermissionGuard;